import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Modal } from "react-bootstrap";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import moment from "moment";

import { fetchHotelDetails, fetchHotelOffersDetails } from "../../../utils/Hote";
import FilterPillsRow from "./FilterPillsRow";
import starIcon from "../../../assets/img/star.png";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import BedIcon from "../../../assets/svg/BedIcon";
import ArrowDownIcon from "../../../assets/svg/ArrowDownIcon";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RefundIcon from "../../../assets/svg/Refund.svg";
import NonRefundIcon from "../../../assets/svg/NonRefund.svg";

const collageStyles = `
.photo-collage {
  position: relative;
  display: grid;
  grid-template-areas:
    "left top-right"
    "left bottom-right";
  grid-template-columns: 2fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 6px;
  height: 400px;
  overflow: hidden;
  border-radius: 10px;
}
.photo-collage .main-image {
  grid-area: left;
}
.photo-collage .top-right {
  grid-area: top-right;
}
.photo-collage .bottom-right {
  grid-area: bottom-right;
}
.photo-collage img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
  cursor: pointer;
}
.collage-arrow {
  position: absolute;
  bottom: 10px;
  z-index: 2;
  font-size: 24px;
  color: #fff;
  background: rgba(0,0,0,0.4);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  text-align: center;
  line-height: 36px;
  cursor: pointer;
}
.collage-arrow-left { left: 10px; }
.collage-arrow-right { right: 10px; }

/* Hide collage on screens narrower than 768px */
@media (max-width: 767px) {
  .photo-collage { display: none; }
}
`;

/** Helper to render star icons */
const renderStars = (count) => {
  const rounded = Math.round(count || 0);
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      {Array.from({ length: rounded }).map((_, i) => (
        <img key={i} src={starIcon} alt="star" style={{ width: 16, height: 16 }} />
      ))}
    </div>
  );
};

/** Helper function to convert currency text to icon */
export const renderCurrency = (currencyCode) => {
  const currencyMap = {
    "EUR": "€",
    "USD": "$",
    "GBP": "£",
    "JPY": "¥",
    "CNY": "¥",
    "RUB": "₽",
    "TRY": "₺",
    "INR": "₹",
    "CHF": "CHF",
    "CAD": "CA$",
    "AUD": "A$"
  };

  // Return the icon if available, otherwise return the original code
  return currencyMap[currencyCode] || currencyCode;
};

/** Helper function to determine refund status and get appropriate icon */
const getRefundStatus = (offer) => {
  // Use the API's isRefundable field directly
  return {
    icon: offer.isRefundable ? RefundIcon : NonRefundIcon,
  };
};

function HotelDetails({ hotelId, routeState, address, checkInDate: propCheckInDate, checkOutDate: propCheckOutDate, translation }) {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { searchId, offerId, hotelSearchData } = state || {};
  const [queryParams] = useSearchParams();
  
  // Implementojmë një objekt searchParams për të simuluar API-n e React Router
  const searchParams = {
    get: (name) => {
      // Provo të lexohet nga queryParams (URL) ose përdor një vlerë default
      return queryParams.get(name) || null;
    }
  };
  
  // Prioritet u jepet datave nga props, pastaj mundësohu të gjendet në state ose në ofertat
  let checkInDate = propCheckInDate || state?.checkInDate;
  let checkOutDate = propCheckOutDate || state?.checkOutDate;
  
  // Nëse datat akoma mungojnë, kontrolloj nëse mund t'i marrim nga hotelSearchData.offers[0]
  if ((!checkInDate || !checkOutDate) && hotelSearchData?.offers && hotelSearchData.offers.length > 0) {
    // Marrja e check-in nga ofertat
    const offerCheckIn = hotelSearchData.offers[0].checkIn;
    if (offerCheckIn && !checkInDate) {
      checkInDate = offerCheckIn;
    }
    
    // Nëse kemi nights, mund të llogarisim check-out
    const nights = hotelSearchData.offers[0].night;
    if (offerCheckIn && nights && !checkOutDate) {
      const checkOut = new Date(offerCheckIn);
      checkOut.setDate(checkOut.getDate() + nights);
      checkOutDate = checkOut.toISOString().split('T')[0];
    }
  }

  // 1) Detect mobile or desktop
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 2) Data states
  const [hotelDetails, setHotelDetails] = useState(null);
  const [offersDetails, setOffersDetails] = useState([]);
  const [filteredOffers, setFilteredOffers] = useState([]);
  const [boardGroupOptions, setBoardGroupOptions] = useState([]);
  const [cancellationOptions, setCancellationOptions] = useState([]);
  const [filters, setFilters] = useState({ boardGroup: "", cancellation: "" });
  const [hoverShowRooms, setHoverShowRooms] = useState(false);


  // Room selection / expansions
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [expandedRooms, setExpandedRooms] = useState({});
  const [showAllRoomTypes, setShowAllRoomTypes] = useState(false);

  // Collage / modal states
  const [showModal, setShowModal] = useState(false);
  const [initialSlide, setInitialSlide] = useState(0);
  const [collageStart, setCollageStart] = useState(0);
  const [hoveredOffer, setHoveredOffer] = useState(null);

  const sliderRef = useRef(null);

  /** 3) Fetch hotel details */
  useEffect(() => {
    if (!hotelId) return;
    (async () => {
      const data = await fetchHotelDetails(hotelId);
      if (data) setHotelDetails(data);
    })();
  }, [hotelId]);

  /** 4) Fetch offers if we have searchId/offerId/hotelId */
  useEffect(() => {
    if (!searchId || !offerId || !hotelId) return;
    (async () => {
      const result = await fetchHotelOffersDetails({
        searchId,
        offerId,
        productType: 2, // hotels
        productId: hotelId,
        currency: "EUR",
        culture: "en-US",
        getRoomInfo: true
      });
      if (result?.offers) {
        setOffersDetails(result.offers);
      }
    })();
  }, [searchId, offerId, hotelId]);

  useEffect(() => {
    const boards = new Set();
    const cancels = new Set();
    offersDetails.forEach((off) => {
      off.rooms?.[0]?.boardGroups?.forEach((bg) => boards.add(bg.name));
      const cpols = off.cancellationPolicies || [];
      if (!cpols.length) {
        cancels.add(translation?.non_refundable || "Non-refundable");
      } else {
        cpols.forEach((p) => {
          if (p.dueDate) {
            const dateStr = new Date(p.dueDate).toLocaleDateString();
            cancels.add(`Free cancellation until ${dateStr}`);
          }
        });
      }
    });
    setBoardGroupOptions([...boards]);
    setCancellationOptions([...cancels]);
  }, [offersDetails]);

  /** 6) Apply filters => filteredOffers */
  useEffect(() => {
    let temp = [...offersDetails];

    if (filters.boardGroup) {
      temp = temp.filter((o) =>
        o.rooms?.[0]?.boardGroups?.some((bg) => bg.name === filters.boardGroup)
      );
    }
    if (filters.cancellation) {
      temp = temp.filter((o) => {
        const cpols = o.cancellationPolicies || [];
        if (!cpols.length) return filters.cancellation === (translation?.non_refundable || "Non-refundable");
        return cpols.some((p) => {
          if (p.dueDate) {
            const ds = new Date(p.dueDate).toLocaleDateString();
            return `Free cancellation until ${ds}` === filters.cancellation;
          }
          return false;
        });
      });
    }
    
    // ✅ Sort offers to prioritize the selected board filter from Results.jsx
    const selectedBoardFromResults = routeState?.selectedBoardFilter;
    if (selectedBoardFromResults) {
      // Sort so that offers matching the pre-selected board appear first
      temp.sort((a, b) => {
        const aBoardName = a.rooms?.[0]?.boardName?.toLowerCase() || "";
        const bBoardName = b.rooms?.[0]?.boardName?.toLowerCase() || "";
        const targetBoard = selectedBoardFromResults.toLowerCase();
        
        const aMatches = aBoardName.includes(targetBoard) || targetBoard.includes(aBoardName);
        const bMatches = bBoardName.includes(targetBoard) || targetBoard.includes(bBoardName);
        
        if (aMatches && !bMatches) return -1; // a comes first
        if (!aMatches && bMatches) return 1;  // b comes first
        return 0; // maintain original order for non-matching items
      });
    }
    
    setFilteredOffers(temp);
  }, [offersDetails, filters, routeState?.selectedBoardFilter]);

  /** Auto-select cheapest room matching the pre-selected board filter */
  useEffect(() => {
    const selectedBoardFromResults = routeState?.selectedBoardFilter;
    if (selectedBoardFromResults && filteredOffers.length > 0 && selectedRooms.length === 0) {
      // Find offers that match the selected board
      const matchingOffers = filteredOffers.filter((offer) => {
        const boardName = offer.rooms?.[0]?.boardName?.toLowerCase() || "";
        const targetBoard = selectedBoardFromResults.toLowerCase();
        return boardName.includes(targetBoard) || targetBoard.includes(boardName);
      });
      
      if (matchingOffers.length > 0) {
        // Find the cheapest matching offer
        const cheapestOffer = matchingOffers.reduce((min, offer) => 
          offer.price?.amount < min.price?.amount ? offer : min
        );
        
        // Auto-select this room
        setSelectedRooms([cheapestOffer]);
      }
    }
  }, [filteredOffers, routeState?.selectedBoardFilter, selectedRooms.length]);

  /** 7) Early returns if no ID or details not loaded */
  if (!hotelId) {
    return (
      <div className="container mt-5" style={{ color: "#003663" }}>
        <h5>No hotel ID provided.</h5>
      </div>
    );
  }
  if (!hotelDetails) {
    return (
      <div className="container mt-5" style={{ color: "#003663" }}>
        <h5>Loading hotel details...</h5>
      </div>
    );
  }

  /** 8) Collage images + grouping of offers, etc. */
  const allImages = hotelDetails.seasons?.[0]?.mediaFiles || [];
  const collageImages = allImages.slice(collageStart, collageStart + 3);
  const canGoPrev = collageStart > 0;
  const canGoNext = collageStart + 3 < allImages.length;

  // group offers by roomName
  const grouped = filteredOffers.reduce((acc, offer) => {
    const rName = offer.rooms?.[0]?.roomName || (translation?.standard_room || "Standard Room");
    if (!acc[rName]) acc[rName] = [];
    acc[rName].push(offer);
    return acc;
  }, {});
  const groupEntries = Object.entries(grouped);
  const visibleEntries = showAllRoomTypes ? groupEntries : groupEntries.slice(0, 3);

  /** 9) Handler fns */
  const toggleExpand = (roomName) => {
    setExpandedRooms((prev) => ({ ...prev, [roomName]: !prev[roomName] }));
  };
  const toggleSelect = (offer) => {
    // Check if room is already selected
    const isSelected = selectedRooms.some((r) => r.offerId === offer.offerId);

    // Update selected rooms
    if (isSelected) {
      setSelectedRooms(selectedRooms.filter((r) => r.offerId !== offer.offerId));
    } else {
      setSelectedRooms([...selectedRooms, offer]);
      
      // After adding a room, scroll to the Book Now button
      setTimeout(() => {
        const bookNowSection = document.querySelector('[data-scroll-target="book-now"]');
        if (bookNowSection) {
          bookNowSection.scrollIntoView({ 
            behavior: 'smooth',
            block: 'center'
          });
        }
      }, 100);
    }
  };
  
  const handleBookNow = () => {
    // Lexojmë roomCriteria nga session storage
    const roomCriteria = JSON.parse(sessionStorage.getItem('roomCriteria')) || null;
    
    // Kontrollo nëse kemi mjaftueshëm dhoma të zgjedhura për të përmbushur kërkesën
    if (roomCriteria && roomCriteria.length > selectedRooms.length) {
      const missingRooms = roomCriteria.length - selectedRooms.length;
      
      // Përdor confirm dialog për të pyetur përdoruesin nëse dëshiron të vazhdojë
      const userConfirm = window.confirm(
        `Fillimisht keni kërkuar ${roomCriteria.length} dhoma, por keni zgjedhur vetëm ${selectedRooms.length}.\n\n` + 
        `Klikoni OK për të vazhduar me ${selectedRooms.length} dhoma që keni zgjedhur.\n` +
        `Klikoni Cancel për t'u kthyer dhe zgjedhur edhe ${missingRooms} dhoma të tjera.`
      );
      
      // Nëse përdoruesi klikon Cancel, ndërprisni procesin
      if (!userConfirm) {
        return;
      }
    }
    
    // Llogarit totalin e dhomave të zgjedhura
    const totalPrice = selectedRooms.reduce((acc, r) => acc + r.price.amount, 0);
    
    // Extract nationality from state or URL params
    const urlParams = new URLSearchParams(state?.urlParams || window.location.search);
    const nationality = state?.nationality || urlParams.get('nationality');
    
    
    // Krijo një strukturë të dhënash ku çdo dhomë nga roomCriteria ka një ofertë korresponduese
    const selectedRoomsWithCriteria = roomCriteria ? 
      roomCriteria.map((criteria, index) => {
        // Assign one of the selected rooms to this criteria
        const room = selectedRooms[index % selectedRooms.length];
        return {
          ...room,
          assignedCriteria: criteria
        };
      }) : 
      selectedRooms; // Nëse nuk ka roomCriteria, vetëm përdorim dhomat e zgjedhura
    
    // Sigurohu që thumbnail është i disponueshëm
    const hotelThumbnail = hotelDetails?.seasons?.[0]?.mediaFiles?.[0]?.urlFull || 
                          hotelDetails?.thumbnailFull || 
                          hotelDetails?.seasons?.[0]?.mediaFiles?.[0]?.fileUrl;
    

    // Calculate nights properly using the correct dates
    const nights = checkInDate && checkOutDate ? 
      Math.ceil(moment(checkOutDate).diff(moment(checkInDate), 'days', true)) : 1;

    // ✅ Add expiration data from route state for offer tracking
    const searchExpiresOn = routeState?.searchExpiresOn;
    
    // ✅ Enhance selected rooms with expiration data from offersDetails
    const selectedRoomsWithExpiration = selectedRoomsWithCriteria.map(room => {
      const matchingOffer = offersDetails.find(offer => offer.offerId === room.offerId);
      return {
        ...room,
        expiresOn: matchingOffer?.expiresOn // Add individual offer expiration
      };
    });

    // Kalojmë në faqen e rezervimit
    navigate("/hotels/booking", {
      state: {
        selectedRooms: selectedRoomsWithExpiration,
        totalPrice,
        hotelName: hotelDetails?.name,
        hotelLocation: hotelDetails?.location?.name || hotelDetails?.address?.addressLines?.join(", ") || (translation?.unknown_location || "Unknown location"),
        checkIn: checkInDate,
        checkOut: checkOutDate,
        nights: nights,
        thumbnail: hotelThumbnail,
        hotelStars: hotelDetails?.stars,
        nationality: nationality,
        roomCriteria,
        searchExpiresOn: searchExpiresOn // ✅ Pass search expiration to booking page
      }
    });
  };
    const getBtnStyle = (offerId, isSelected) => ({
    backgroundColor: isSelected ? "#003663" : "#FBD316",
    color: isSelected ? "#FBD316" : "#003663",
    border: "1px solid #003663",
    borderRadius: 8,
    padding: "0.5rem 1rem",
    cursor: "pointer",
    transition: "transform 0.15s",
    transform: hoveredOffer === offerId ? "scale(1.05)" : "scale(1)",
    fontWeight: 600
  });

  // mobile slider settings
  const mobileSliderSettings = {
    dots: true,
    infinite: true,
    speed: 300,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: true,
    arrows: false,
    nextArrow: null,
    prevArrow: null,
    appendDots: dots => (
      <ul style={{ margin: 0, padding: 0, display: 'flex', justifyContent: 'center', gap: '8px' }}>
        {[...Array(8)].map((_, index) => (
          <li key={index} style={{ margin: 0, width: '6px', height: '6px' }}>
            <div style={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              backgroundColor: '#fff',
              border: '1px solid #003663',
              transition: 'all 0.3s ease',
              opacity: 0.3
            }} />
          </li>
        ))}
      </ul>
    ),
    customPaging: function(i) {
      return (
        <div style={{
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          backgroundColor: '#fff',
          border: '1px solid #003663',
          transition: 'all 0.3s ease'
        }} />
      );
    }
  };

  /** 10) Render UI */
  return (
    <div className="container mb-5" style={{ color: "#003663" }}>
      <style>{collageStyles}</style>
      <style>
        {`
          .slick-slider {
            margin: 0;
            position: relative;
          }
          .slick-arrow {
            display: none !important;
          }
          .slick-dots {
            display: flex !important;
            justify-content: center;
            align-items: center;
            margin-top: 20px !important;
            margin-bottom: 20px !important;
          }
          .slick-dots li {
            margin: 0 !important;
            width: 6px !important;
            height: 6px !important;
            padding: 0 !important;
            transition: all 0.3s ease !important;
          }
          .slick-dots li div {
            width: 100%;
            height: 100%;
            border-radius: 50% !important;
            background-color: #fff;
            border: 1px solid #003663;
            transition: all 0.3s ease !important;
          }
          .slick-dots li.slick-active div {
            background-color: #003663 !important;
            border-color: #003663 !important;
            transform: scale(1.5);
          }
        `}
      </style>

      {/* HEADER CONTAINER */}
      <div
        className="bg-white p-4 rounded shadow mb-4 d-flex"
        style={{
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "flex-start" : "center",
          justifyContent: "space-between",
          gap: isMobile ? "1rem" : 0,
          boxShadow: "0 8px 24px rgba(0, 54, 99, 0.25)",
          borderRadius: "16px"
        }}
      >
        <div>
          {renderStars(hotelDetails.stars)}
          <h2 className="fw-bold mb-1 mt-1">{hotelDetails.name}</h2>
          <div className="d-flex align-items-center mb-2" style={{ flexWrap: isMobile ? "wrap" : "nowrap", gap: isMobile ? "8px" : "0" }}>
            <div style={{ 
              display: "flex", 
              alignItems: "flex-start",
              flexShrink: 0,
              marginRight: isMobile ? "0" : "4px",
              maxWidth: isMobile ? "100%" : "500px",
              wordBreak: "break-word",
              lineHeight: "1.4"
            }}>
              <LocationOnIcon style={{ 
                color: "#003663",
                fontSize: "18px",
                marginRight: "4px",
                marginTop: "2px",
                flexShrink: 0
              }} />
              <span style={{ color: '#003663', fontSize: "14px", fontWeight: 500 }}>
                {hotelDetails.address?.addressLines?.join(", ") || (translation?.no_address_available || "No address available")}
              </span>
            </div>
            {!isMobile && <span style={{ margin: "0 8px", color: "#003663", fontSize: "14px" }}>|</span>}
            <div style={{ 
              display: "flex", 
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap"
            }}>
              <span 
                style={{ 
                  color: '#003663', 
                  cursor: 'pointer', 
                  textDecoration: 'underline', 
                  fontWeight: 500,
                  fontSize: "14px"
                }}
                onClick={() => {
                  const mapSection = document.querySelector('[title="Hotel Location"]');
                  mapSection && mapSection.scrollIntoView({ behavior: "smooth" });
                }}
              >
                Show on map
              </span>
              {!isMobile && <span style={{ margin: "0 8px", color: "#003663", fontSize: "14px" }}>|</span>}
              <span 
                style={{ 
                  color: '#003663', 
                  cursor: 'pointer', 
                  textDecoration: 'underline', 
                  fontWeight: 500,
                  fontSize: "14px"
                }}
                onClick={() => {
                  const descriptionSection = document.getElementById('hotel-description');
                  descriptionSection && descriptionSection.scrollIntoView({ behavior: "smooth" });
                }}
              >
                What's nearby?
              </span>
            </div>
          </div>
        </div>
        <div style={{ textAlign: "right", marginLeft: "auto" }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'flex-end',
            gap: '12px'
          }}>
            {offersDetails[0]?.price?.amount && (
              <div style={{ 
                fontWeight: 'bold',
                whiteSpace: 'nowrap'
              }}>
                from {offersDetails[0].price.amount.toFixed(2)} €
              </div>
            )}
            <button
              className="btn"
              onMouseEnter={() => setHoverShowRooms(true)}
              onMouseLeave={() => setHoverShowRooms(false)}
              onClick={() => {
                const el = document.getElementById("rooms-section");
                el && el.scrollIntoView({ behavior: "smooth" });
              }}
              style={{
                backgroundColor: "#FBD215",
                color: "#003663",
                border: "none",
                borderRadius: 32,
                padding: "0.75rem 1.5rem",
                fontWeight: 600,
                boxShadow: "0px 6px 12px rgba(0,0,0,0.25)",
                transform: hoverShowRooms ? "scale(1.10)" : "scale(1)",
                transition: "transform 0.2s ease-in-out",
                width: isMobile ? '120px' : '180px',
                height: '45px',
                textAlign: 'center',
                fontWeight: 700,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                fontSize: isMobile ? '14px' : '16px',
                letterSpacing: '0.5px',
                whiteSpace: 'nowrap'
              }}
            >
              <span style={{ margin: 0 }}>
                Show rooms
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* IMAGES AND ROOMS CONTAINER */}
      <div className="bg-white rounded shadow mb-4" style={{ 
        borderRadius: "16px", 
        overflow: "hidden", 
        backgroundColor: "#fff",
        position: "relative" 
      }}>
        {/* Image Gallery Section */}
        <div className="p-3" style={{ overflow: "hidden" }}>
          {isMobile ? (
            <Slider {...mobileSliderSettings}>
              {allImages.map((img, idx) => (
                <div key={idx} style={{ 
                  width: "100%",
                  height: "400px", // Fixed height for mobile
                  position: "relative",
                  padding: "0 8px",
                  marginBottom: "16px"
                }}>
                  <img
                    src={img.urlFull}
                    alt={`Slide ${idx}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "12px"
                    }}
                  />
                  <div style={{
                    position: "absolute",
                    bottom: "12px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    color: "white",
                    fontSize: "12px",
                    background: "rgba(0, 0, 0, 0.6)",
                    padding: "6px 12px",
                    borderRadius: "20px",
                    zIndex: 1
                  }}>
                    {`${idx + 1} of ${allImages.length}`}
                  </div>
                </div>
              ))}
            </Slider>
          ) : (
            <div className="photo-collage">
              {collageImages[0] && (
                <div className="main-image">
                  <img
                    src={collageImages[0].urlFull}
                    alt="collage main"
                    onClick={() => {
                      setInitialSlide(collageStart);
                      setShowModal(true);
                    }}
                  />
                </div>
              )}
              {collageImages[1] && (
                <div className="top-right">
                  <img
                    src={collageImages[1].urlFull}
                    alt="collage top right"
                    onClick={() => {
                      setInitialSlide(collageStart + 1);
                      setShowModal(true);
                    }}
                  />
                </div>
              )}
              {collageImages[2] && (
                <div className="bottom-right">
                  <img
                    src={collageImages[2].urlFull}
                    alt="collage bottom right"
                    onClick={() => {
                      setInitialSlide(collageStart + 2);
                      setShowModal(true);
                    }}
                  />
                </div>
              )}
              {canGoPrev && (
                <div
                  className="collage-arrow collage-arrow-left"
                  onClick={() => setCollageStart((v) => v - 3)}
                >
                  ‹
                </div>
              )}
              {canGoNext && (
                <div
                  className="collage-arrow collage-arrow-right"
                  onClick={() => setCollageStart((v) => v + 3)}
                >
                  ›
                </div>
              )}
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="px-3 py-3" style={{ backgroundColor: "#fff" }}>
          <FilterPillsRow
            boardGroupOptions={boardGroupOptions}
            cancellationOptions={cancellationOptions}
            filters={filters}
            onChangeFilters={setFilters}
            isMobile={isMobile}
          />
        </div>

        {/* Rooms Section */}
        <div
          id="rooms-section"
          style={{
            paddingTop: "0",
            paddingBottom: "20px",
            backgroundColor: "#fff",
            borderTop: "none"
          }}
        >
          <div className="px-3 mb-4">
            <h4 className="fw-bold mb-4">{translation?.available_rooms || "Available rooms"}</h4>
            
            {/* Room header - only show on desktop */}
            {!isMobile && (
              <div
                className="d-flex justify-content-between align-items-center px-3 py-2 mb-4"
                style={{
                  borderBottom: "4px solid #003663",
                  fontWeight: 600,
                  color: "#003663",
                  borderRadius: "6px",
                  backgroundColor: "#fff"
                }}
              >
                <div style={{ flex: 3 }}>{translation?.room || "Room"}</div>
                <div style={{ flex: 2 }}>{translation?.meals || "Meals"}</div>
                <div style={{ flex: 2 }}>{translation?.cancellation || "Cancellation"}</div>
                <div style={{ flex: 2 }}>{translation?.price || "Price"}</div>
                <div style={{ flex: 1 }} />
              </div>
            )}
          </div>

          {/* Room cards container */}
          <div style={{ padding: "0 16px", width: "100%", backgroundColor: "#fff" }}>
            {filteredOffers.slice(0, showAllRoomTypes ? filteredOffers.length : 3).map((offer, i) => {
              const room = offer.rooms?.[0];
              const cp = offer.cancellationPolicies || [];
              const cText = !cp.length
                ? (translation?.non_refundable || "Non-refundable")
                : `${cp[0]?.price?.amount > 0 ? cp[0]?.price?.amount + "€" : "0€"} until ${new Date(cp[0].dueDate).toLocaleDateString()}`;
              const isSelected = selectedRooms.some((r) => r.offerId === offer.offerId);

              return isMobile ? (
                // Mobile card design
                <div 
                  key={i}
                  style={{
                    backgroundColor: "#fff",
                    borderRadius: "16px",
                    boxShadow: "0 4px 12px rgba(0, 54, 99, 0.15)",
                    marginBottom: "16px",
                    padding: "16px",
                    border: "1px solid rgba(0, 54, 99, 0.1)"
                  }}
                >
                  <div className="d-flex align-items-start gap-3 mb-3">
                    <div style={{ color: "#003663" }}>
                      <BedIcon />
                    </div>
                    <div>
                      <h6 style={{ 
                        fontSize: "18px", 
                        fontWeight: "600",
                        color: "#003663",
                        marginBottom: "4px"
                      }}>
                        {room?.roomName || (translation?.standard_room || "Standard Room")}
                      </h6>
                      <p style={{ 
                        fontSize: "14px",
                        color: "#666",
                        marginBottom: "8px"
                      }}>
                        {room?.roomDescription || "Non-smoking, Window"}
                      </p>
                    </div>
                  </div>

                  <div style={{ 
                    borderTop: "1px solid #eee",
                    borderBottom: "1px solid #eee",
                    padding: "12px 0",
                    marginBottom: "12px"
                  }}>
                    <div className="d-flex justify-content-between mb-2">
                      <span style={{ color: "#666", fontSize: "14px" }}>Meals:</span>
                      <span style={{ color: "#003663", fontWeight: "500" }}>
                        {room?.boardName === "ROOM ONLY" ? (translation?.not_included || "Not included") : room?.boardName}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span style={{ color: "#666", fontSize: "14px" }}>Cancellation:</span>
                      <span style={{ color: "#003663", fontWeight: "500", textAlign: "right" }}>
                        {cText}
                      </span>
                    </div>
                  </div>

                  <div className="d-flex justify-content-between align-items-center">
                    <div style={{ 
                      fontSize: "24px", 
                      fontWeight: "700",
                      color: "#003663"
                    }}>
                      {offer.price.amount.toFixed(2)} {renderCurrency(offer.price.currency)}
                    </div>
                    <button
                      style={{
                        backgroundColor: isSelected ? "#003663" : "#FBD316",
                        color: isSelected ? "#FBD316" : "#003663",
                        border: "none",
                        borderRadius: "999px",
                        padding: "12px 24px",
                        display: "flex",
                        alignItems: "center",
                        fontWeight: 600,
                        boxShadow: "0px 4px 8px rgba(0,0,0,0.12)",
                        transform: hoveredOffer === offer.offerId ? "scale(1.05)" : "scale(1)",
                        transition: "transform 0.2s ease-in-out",
                      }}
                      onClick={() => toggleSelect(offer)}
                      onMouseEnter={() => setHoveredOffer(offer.offerId)}
                      onMouseLeave={() => setHoveredOffer(null)}
                    >
                      {isSelected ? (translation?.selected || "Selected") : (translation?.select || "Select")}
                      <ArrowForwardIosIcon style={{ fontSize: "0.8rem", marginLeft: 8 }} />
                    </button>
                  </div>
                </div>
              ) : (
                // Desktop table row design
                <div 
                  key={i} 
                  className="d-flex justify-content-between align-items-center p-3"
                  style={{
                    backgroundColor: "#fff",
                    borderRadius: "16px",
                    boxShadow: "0 8px 15px -4px rgba(0, 54, 99, 0.3)", 
                    color: "#003663",
                    border: "1px solid rgba(0, 54, 99, 0.05)",
                    marginBottom: "12px",
                  }}
                >
                  {/* Room info */}
                  <div style={{ flex: 3 }} className="d-flex gap-3 align-items-start">
                    <div style={{ flexShrink: 0, color: "#003663" }}>
                      <BedIcon />
                    </div>
                    <div>
                      <div className="fw-bold" style={{ fontSize: "16px" }}>{room?.roomName || (translation?.standard_room || "Standard Room")}</div>
                      <div className="text-muted" style={{ fontWeight: 400, fontSize: 14 }}>
                        {room?.roomDescription || "Non-smoking, Window"}
                      </div>
                    </div>
                  </div>

                  {/* Meals */}
                  <div style={{ flex: 2, fontWeight: 500 }}>
                    {room?.boardName === "ROOM ONLY" ? (translation?.not_included || "Not included") : room?.boardName}
                  </div>

                  {/* Cancellation */}
                  <div style={{ flex: 2, display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <div style={{ textAlign: "center", marginBottom: "12px" }}>{cText}</div>
                  </div>

                  {/* Price */}
                  <div style={{ flex: 2, fontWeight: 700, fontSize: "18px" }}>
                    {offer.price.amount.toFixed(2)} {renderCurrency(offer.price.currency)}
                  </div>

                  <div style={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
                    <button
                      style={{
                        backgroundColor: isSelected ? "#003663" : "#FBD316",
                        color: isSelected ? "#FBD316" : "#003663",
                        border: "none",
                        borderRadius: "999px",
                        padding: "12px 24px",
                        display: "flex",
                        alignItems: "center",
                        fontWeight: 600,
                        boxShadow: "0px 4px 8px rgba(0,0,0,0.12)",
                        transform: hoveredOffer === offer.offerId ? "scale(1.05)" : "scale(1)",
                        transition: "transform 0.2s ease-in-out",
                      }}
                      onClick={() => toggleSelect(offer)}
                      onMouseEnter={() => setHoveredOffer(offer.offerId)}
                      onMouseLeave={() => setHoveredOffer(null)}
                    >
                      {isSelected ? (translation?.selected || "Selected") : (translation?.select || "Select")}
                      <ArrowForwardIosIcon style={{ fontSize: "0.8rem", marginLeft: 8 }} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Show more rooms button - MOVED OUTSIDE */}
      {filteredOffers.length > 3 && (
        <div
          className="text-center mt-2 mb-4"
          style={{ cursor: "pointer", color: "#003663", fontWeight: 600 }}
          onClick={() => setShowAllRoomTypes((prev) => !prev)}
        >
          <ArrowDownIcon style={{ marginRight: 8, verticalAlign: "middle" }} />
          {showAllRoomTypes
            ? (translation?.show_fewer_rooms || "Show fewer rooms")
            : `${translation?.show_more_rooms || "Show more rooms"} (${filteredOffers.length - 3})`}
        </div>
      )}

      {selectedRooms.length > 0 && (
        <div 
          className="bg-white p-4 rounded shadow mb-4" 
          style={{ borderRadius: "16px" }}
          data-scroll-target="book-now"
        >
          <h5 style={{ color: "#003663" }} className="text-end">
            {translation?.total || "Total"}:{" "}
            {selectedRooms.reduce((sum, r) => sum + (r.price.amount || 0), 0).toFixed(2)}{" "}
            {renderCurrency(selectedRooms[0]?.price.currency)}
          </h5>
          <div className="text-end">
            <button
              className="btn btn-lg"
              style={{
                backgroundColor: "#FBD316",
                color: "#003663",
                border: "none",
                borderRadius: "999px",
                padding: "16px 32px",
                fontWeight: "800",
                fontSize: "1.1rem",
                boxShadow: "0px 4px 8px rgba(0,0,0,0.12)",
                letterSpacing: "0.5px",
                textTransform: "uppercase"
              }}
              onClick={handleBookNow}
              disabled={selectedRooms.length === 0}
            >
              {translation?.book_now || "Book Now"} ({selectedRooms.length})
            </button>
          </div>
        </div>
      )}
      
      {/* Hotel Description Container */}
      <div id="hotel-description" className="bg-white p-4 rounded shadow mb-4" style={{ borderRadius: "16px" }}>
        <h4 className="fw-bold mb-4" style={{ color: "#003663" }}>
          {translation?.hotel_information || "Hotel Information"}
        </h4>
        
        {hotelDetails.seasons?.[0]?.textCategories?.map((category, index) => (
          <div 
            key={index} 
            className="mb-4 pb-4" 
            style={{ 
              borderBottom: index !== hotelDetails.seasons[0].textCategories.length - 1 ? "1px solid rgba(0, 54, 99, 0.1)" : "none"
            }}
          >
            <div className="d-flex align-items-center gap-2 mb-3">
              {/* Category-specific icons */}
              <div style={{ 
                width: 40, 
                height: 40, 
                backgroundColor: "#FBD316", 
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0
              }}>
                {category.name === "Location" && <LocationOnIcon style={{ color: "#003663" }} />}
                {category.name === "Facilities" && (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 7H5C3.89543 7 3 7.89543 3 9V18C3 19.1046 3.89543 20 5 20H19C20.1046 20 21 19.1046 21 18V9C21 7.89543 20.1046 7 19 7Z" stroke="#003663" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 20V5C16 4.46957 15.7893 3.96086 15.4142 3.58579C15.0391 3.21071 14.5304 3 14 3H10C9.46957 3 8.96086 3.21071 8.58579 3.58579C8.21071 3.96086 8 4.46957 8 5V20" stroke="#003663" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
                {category.name === "Rooms" && <BedIcon />}
                {category.name === "Sports/Entertainment" && (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#003663" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 6V12L16 14" stroke="#003663" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
                {category.name === "Meals" && (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 8H19C20.0609 8 21.0783 8.42143 21.8284 9.17157C22.5786 9.92172 23 10.9391 23 12C23 13.0609 22.5786 14.0783 21.8284 14.8284C21.0783 15.5786 20.0609 16 19 16H18" stroke="#003663" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M2 8H18V17C18 18.0609 17.5786 19.0783 16.8284 19.8284C16.0783 20.5786 15.0609 21 14 21H6C4.93913 21 3.92172 20.5786 3.17157 19.8284C2.42143 19.0783 2 18.0609 2 17V8Z" stroke="#003663" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M6 1V4" stroke="#003663" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M10 1V4" stroke="#003663" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M14 1V4" stroke="#003663" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
                {category.name === "Payment" && (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21 4H3C1.89543 4 1 4.89543 1 6V18C1 19.1046 1.89543 20 3 20H21C22.1046 20 23 19.1046 23 18V6C23 4.89543 22.1046 4 21 4Z" stroke="#003663" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M1 10H23" stroke="#003663" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              <h5 className="fw-bold m-0" style={{ color: "#003663" }}>
                {category.name}
              </h5>
            </div>
            
            {category.presentations?.map((presentation, pIndex) => (
              <div 
                key={pIndex} 
                style={{ 
                  color: "#003663",
                  fontSize: "15px",
                  lineHeight: "1.6",
                  paddingLeft: "52px" // Aligns with icon
                }}
              >
                {presentation.text}
              </div>
            ))}
          </div>
        ))}
        
        {/* Facilities Grid */}
        {hotelDetails.seasons?.[0]?.facilityCategories?.map((category, index) => (
          <div key={index} className="mt-5">
            <h5 className="fw-bold mb-3" style={{ color: "#003663" }}>
              {category.name} Facilities
            </h5>
            <div className="d-flex flex-wrap gap-2">
              {category.facilities?.map((facility, fIndex) => (
                <div
                  key={fIndex}
                  style={{
                    backgroundColor: "#f8f9fa",
                    padding: "8px 16px",
                    borderRadius: "20px",
                    color: "#003663",
                    fontSize: "14px",
                    border: "1px solid rgba(0, 54, 99, 0.1)"
                  }}
                >
                  {facility.name}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      
      {/* Location Container */}
      {hotelDetails.geolocation?.latitude && (
        <div id="location-section" className="bg-white p-4 rounded shadow mb-4" style={{ borderRadius: "16px" }}>
          <h4 className="fw-bold mb-3" style={{ color: "#003663" }}>
            {translation?.location || "Location"}
          </h4>
          <iframe
            title="Hotel Location"
            width="100%"
            height="350"
            style={{ border: 0, borderRadius: 8 }}
            src={`https://maps.google.com/maps?q=${hotelDetails.geolocation.latitude},${hotelDetails.geolocation.longitude}&output=embed`}
            allowFullScreen
          />
        </div>
      )}

      {/* Images Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="xl"
        centered
        fullscreen={true}
        dialogClassName="image-modal-dialog"
      >
        <style>
          {`
            .image-modal-dialog {
              max-width: 100% !important;
              margin: 0 !important;
              height: 100vh;
            }
            .modal-content {
              background-color: rgba(0, 54, 99, 0.4) !important;
              border: none !important;
              height: 100vh;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .modal-backdrop.show {
              opacity: 0.7 !important;
              background-color: rgba(0, 54, 99, 0.2) !important;
            }
            .slick-slider {
              width: 90%;
              max-width: 1200px;
              margin: 0 auto;
              position: relative;
            }
            .slick-track {
              display: flex !important;
              align-items: center !important;
            }
            .slick-slide {
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
            }
            .modal-header {
              position: absolute;
              right: 0;
              top: 0;
              z-index: 1000;
              border: none;
              background: transparent;
            }
            .modal-header .close {
              color: white;
              opacity: 0.8;
              transition: opacity 0.2s;
            }
            .modal-header .close:hover {
              opacity: 1;
            }
            .slick-arrow {
              display: none !important;
            }
          `}
        </style>

        <Modal.Header>
          <button 
            onClick={() => setShowModal(false)}
            style={{
              background: "transparent",
              border: "none",
              color: "#fff",
              fontSize: "24px",
              cursor: "pointer",
              padding: "20px",
              opacity: 0.8,
              transition: "opacity 0.2s"
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = "1"}
            onMouseLeave={(e) => e.currentTarget.style.opacity = "0.8"}
          >
            <CloseIcon />
          </button>
        </Modal.Header>

        <div style={{ 
          position: "relative",
          width: "90%",
          maxWidth: "1400px",
          margin: "0 auto",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}>
          <Slider
            ref={sliderRef}
            initialSlide={initialSlide}
            dots={false}
            infinite={true}
            speed={500}
            slidesToShow={1}
            slidesToScroll={1}
            arrows={false}
            nextArrow={null}
            prevArrow={null}
          >
            {allImages.map((img, idx) => (
              <div key={idx} style={{ 
                height: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative"
              }}>
                <div style={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}>
                  <img
                    src={img.urlFull}
                    alt={`Slide ${idx}`}
                    style={{
                      width: "900px",
                      height: "600px",
                      objectFit: "cover",
                      margin: "auto",
                      borderRadius: "8px"
                    }}
                  />
                  {/* Navigation arrows */}
                  <div
                    onClick={() => sliderRef.current?.slickPrev()}
                    style={{
                      position: "absolute",
                      left: "-80px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      zIndex: 2,
                      cursor: "pointer",
                      color: "#003663",
                      width: 50,
                      height: 50,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "rgba(255, 255, 255, 0.85)",
                      borderRadius: "50%",
                      transition: "all 0.3s ease",
                      opacity: 0.8,
                      boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "rgba(255, 255, 255, 0.95)";
                      e.currentTarget.style.transform = "translateY(-50%) scale(1.1)";
                      e.currentTarget.style.opacity = "1";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "rgba(255, 255, 255, 0.85)";
                      e.currentTarget.style.transform = "translateY(-50%) scale(1)";
                      e.currentTarget.style.opacity = "0.8";
                    }}
                  >
                    <ChevronLeftIcon style={{ fontSize: 35 }} />
                  </div>
                  <div
                    onClick={() => sliderRef.current?.slickNext()}
                    style={{
                      position: "absolute",
                      right: "-80px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      zIndex: 2,
                      cursor: "pointer",
                      color: "#003663",
                      width: 50,
                      height: 50,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "rgba(255, 255, 255, 0.85)",
                      borderRadius: "50%",
                      transition: "all 0.3s ease",
                      opacity: 0.8,
                      boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "rgba(255, 255, 255, 0.95)";
                      e.currentTarget.style.transform = "translateY(-50%) scale(1.1)";
                      e.currentTarget.style.opacity = "1";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "rgba(255, 255, 255, 0.85)";
                      e.currentTarget.style.transform = "translateY(-50%) scale(1)";
                      e.currentTarget.style.opacity = "0.8";
                    }}
                  >
                    <ChevronRightIcon style={{ fontSize: 35 }} />
                  </div>
                </div>
              </div>
            ))}
          </Slider>
        </div>
      </Modal>
    </div>
  )
}

export default HotelDetails